/**
 * PwaUpdatePrompt — surfaces an "Update available" snackbar when a new
 * service worker has been installed but not yet activated (because the
 * current page is still controlled by the old one).
 *
 * Listens for a 'pwa:update-available' CustomEvent dispatched by
 * `serviceWorkerRegistration.ts` when the SW lifecycle reports
 * a waiting/installed worker. The "Refresh" button posts SKIP_WAITING
 * to the waiting worker, then reloads — which immediately swaps the
 * controlling SW and gets the user onto the new build.
 */
import React, { useEffect, useState } from 'react';
import { Snackbar, Box, Button, Typography, IconButton } from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import CloseIcon from '@mui/icons-material/Close';

const PwaUpdatePrompt: React.FC = () => {
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const sw = (e as CustomEvent<ServiceWorker | null>).detail;
      if (sw) setWaitingWorker(sw);
    };
    window.addEventListener('pwa:update-available', handler as EventListener);
    return () => window.removeEventListener('pwa:update-available', handler as EventListener);
  }, []);

  const handleRefresh = () => {
    try {
      // Ask the waiting SW to take over immediately
      if (waitingWorker) {
        waitingWorker.postMessage('SKIP_WAITING');
      }
    } catch { /* ignore */ }
    // Reload once the controller changes (or after a short fallback)
    try {
      let didReload = false;
      const onControllerChange = () => {
        if (didReload) return;
        didReload = true;
        window.location.reload();
      };
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.addEventListener('controllerchange', onControllerChange, { once: true });
      }
      setTimeout(() => { if (!didReload) window.location.reload(); }, 1200);
    } catch {
      window.location.reload();
    }
  };

  if (!waitingWorker) return null;

  return (
    <Snackbar
      open
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      sx={{ zIndex: 2000 }}
    >
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
        boxShadow: 4, borderRadius: 1.5, p: 1.5, maxWidth: 380,
      }}>
        <GetAppIcon color="primary" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
            App update available
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Refresh to get the latest version.
          </Typography>
        </Box>
        <Button size="small" variant="contained" onClick={handleRefresh}>Refresh</Button>
        <IconButton size="small" onClick={() => setWaitingWorker(null)} sx={{ p: 0.5 }} aria-label="Dismiss">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </Snackbar>
  );
};

export default PwaUpdatePrompt;
